import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaEuroSign, FaArrowUp, FaArrowDown, FaReceipt } from 'react-icons/fa6';
import BankAccountsChart from '../../components/charts/BankAccountsChart';
import ContributionChart from '../../components/charts/ContributionChart';
import ProductSalesChart from '../../components/charts/ProductSalesChart';
import MemberBalanceCards from '../../components/charts/MemberBalanceCards';
import IncomeExpensesChart from '../../components/charts/IncomeExpensesChart';
import TransactionProcessingCard from '../../components/cards/TransactionProcessingCard';

import PageHeader from '../../components/misc/PageHeader';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import * as bankAccountService from '../../../services/bankAccountService';
import { BankAccount, CreateBankAccountModel } from '../../../types/bankAccount';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import CreateBankAccountModal from '../../components/modals/CreateBankAccountModal';

interface FinancialOverviewPageProps {
  hideHeader?: boolean;
}

interface FinancialMetrics {
  totalBalance: number;
  totalIncome: number;
  totalExpenses: number;
  unprocessedMutations: number;
  connectedAccounts: number;
  disconnectedAccounts: number;
}

export default function FinancialOverviewPage({
  hideHeader = false,
}: FinancialOverviewPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.overview' });
  const business = useSelectedBusiness();

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [metrics, setMetrics] = useState<FinancialMetrics>({
    totalBalance: 0,
    totalIncome: 0,
    totalExpenses: 0,
    unprocessedMutations: 0,
    connectedAccounts: 0,
    disconnectedAccounts: 0,
  });
  const [loading, setLoading] = useState(true);
  const [createAccountModalOpen, setCreateAccountModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (business) {
      loadFinancialData();
    }
  }, [business]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      const [accounts, transactions] = await Promise.all([
        bankAccountService.getAllBankAccounts(),
        bankAccountService.getAllTransactions(),
      ]);

      setBankAccounts(accounts);

      // Calculate metrics
      const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);
      const totalIncome = transactions
        .filter((transaction) => transaction.amount > 0)
        .reduce((sum, transaction) => sum + transaction.amount, 0);
      const totalExpenses = transactions
        .filter((transaction) => transaction.amount < 0)
        .reduce((sum, transaction) => sum + Math.abs(transaction.amount), 0);

      const connectedAccounts = accounts.filter(
        (acc) => acc.requisitionId && acc.transferCompleted,
      ).length;
      const disconnectedAccounts = accounts.length - connectedAccounts;

      // Get unprocessed mutations count
      const mutationPromises = accounts.map(async (account) => {
        try {
          const mutations = await bankAccountService.getAllMutationsForBank(account.accountId);
          return mutations.filter((m) => !m.bankTransactionId).length;
        } catch (error) {
          console.error('Error loading mutations for account:', account.accountId);
          return 0;
        }
      });

      const unprocessedCounts = await Promise.all(mutationPromises);
      const totalUnprocessed = unprocessedCounts.reduce((sum, count) => sum + count, 0);

      setMetrics({
        totalBalance,
        totalIncome,
        totalExpenses,
        unprocessedMutations: totalUnprocessed,
        connectedAccounts,
        disconnectedAccounts,
      });
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // removed unused handleBankAccountClick

  const handleCreateBankAccountSuccess = (newData: CreateBankAccountModel) => {
    setCreateAccountModalOpen(false);
    bankAccountService
      .createBankAccount(newData)
      .then((ba) => {
        // Add the new account to the existing list
        setBankAccounts((prev) => [...prev, ba]);
        toast.success('Bank account created successfully');
        // Reload financial data to update metrics
        loadFinancialData();
      })
      .catch(() => {
        toast.error('Failed to create bank account');
      });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  return (
    <>
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-secondary-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('cards.totalBalance')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatEuroCents(metrics.totalBalance, i18n.language)}
              </p>
            </div>
            <div className="rounded-full bg-blue-50 p-3">
              <FaEuroSign className="h-6 w-6 text-primary-300" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-secondary-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('cards.totalIncome')}</p>
              <p className="text-2xl font-bold text-success">
                {formatEuroCents(metrics.totalIncome, i18n.language)}
              </p>
            </div>
            <div className="rounded-full bg-green-50 p-3">
              <FaArrowUp className="h-6 w-6 text-success" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-secondary-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('cards.totalExpenses')}</p>
              <p className="text-2xl font-bold text-danger">
                {formatEuroCents(metrics.totalExpenses, i18n.language)}
              </p>
            </div>
            <div className="rounded-full bg-red-50 p-3">
              <FaArrowDown className="h-6 w-6 text-danger" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-secondary-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('cards.unprocessed')}</p>
              <p className="text-2xl font-bold text-secondary">{metrics.unprocessedMutations}</p>
            </div>
            <div className="rounded-full bg-secondary-50 p-3">
              <FaReceipt className="h-6 w-6 text-secondary" />
            </div>
          </div>
        </div>
      </div>

      {/* Bank Accounts Chart */}
      <div className="mt-8">
        <BankAccountsChart
          bankAccounts={bankAccounts}
          onCreateAccount={() => setCreateAccountModalOpen(true)}
        />
      </div>

      {/* Transaction Processing Card */}
      <TransactionProcessingCard onDataReload={loadFinancialData} />

      {/* Contribution and Product tables side-by-side */}
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <ContributionChart />
        <ProductSalesChart />
      </div>

      {/* Member Balance Cards */}
      <MemberBalanceCards />

      {/* Income and Expenses Chart */}
      <div className="mt-8">
        <IncomeExpensesChart bankAccounts={bankAccounts} />
      </div>

      <CreateBankAccountModal
        open={createAccountModalOpen}
        setOpen={setCreateAccountModalOpen}
        onSuccess={handleCreateBankAccountSuccess}
      />
    </>
  );
}

